import 'reflect-metadata';
import type { ClientMetaProvider, ClientMetaPayload } from './ClientMetaProvider';
import ConfigService from './config/ConfigService';
import type Engine from './engine';
import type { IntegrationsService } from './engine/IntegrationsService';
import type NetworkTransportPolicyManager from './engine/network/NetworkTransportPolicyManager';
import type Peer from './engine/Peer';
import type { TransportsWebRTCStats } from './engine/WebRTCStats/types';
import EnhancedEventEmitter from './EnhancedEventEmitter';
import OperationError from './errors/OperationError';
import { container } from './inversify.config';
import { TOKEN } from './inversify.tokens';
import type { ClientParams } from './types/client';
import type {
  AvailableMediaDevices,
  CreateCameraVideoTrackOptions,
  CreateCustomAudioOptions,
  CreateCustomVideoOptions,
  CreateMicrophoneAudioTrackOptions,
  CreateScreenMediaOptions,
  JoinChannelParams,
  Role,
  TransportsStateInfo,
} from './types/common';
import type { ClientObserverEvents } from './types/engine';
import type { AudioTrack, Track, VideoTrack } from './types/media';
import type { LDTransport } from './types/mediasoup';
import type { IceTransportPolicy } from './types/network';
import type { ScoresCalculator } from './qoe/ScoresCalculator';

/**
 * Основная точка входа в SDK LiveDigital.
 *
 * Экземпляр `Client` отвечает за подключение к каналу, управление локальными
 * треками, получение удалённых участников и подписку на пользовательские события.
 *
 * @example
 * ```ts
 * import Client from '@livedigital/client';
 *
 * const client = new Client({
 *   signalingServerUrl: 'https://signaling.livedigital.space',
 * });
 *
 * // Подписки — до join(), чтобы не пропустить события
 * client.observer.on('peer-joined', (peer) => {
 *   peer.observer.on('media-published', async ({ producerId }) => {
 *     await peer.subscribe({ producerId });
 *   });
 * });
 *
 * client.observer.on('channel-state-synced', async () => {
 *   // Состояние канала синхронизировано — можно работать с участниками
 * });
 *
 * await client.join({ token: 'signaling-token' });
 * client.requestChannelStateSync();
 *
 * const cameraTrack = await client.createCameraVideoTrack();
 * await cameraTrack.publish();
 * ```
 */
class Client {
  readonly #engine: Engine;

  readonly #networkTransportPolicyManager: NetworkTransportPolicyManager;

  readonly #clientMetaProvider: ClientMetaProvider;

  private readonly clientEventEmitter: EnhancedEventEmitter;

  readonly canUseNoiseSuppression = true;

  constructor(params: ClientParams) {
    const { clientEventEmitter, internalEventEmitter } = params;

    container
      .bind<ConfigService>(TOKEN.Config)
      .toDynamicValue(() => new ConfigService(params))
      .inSingletonScope();

    container
      .bind<EnhancedEventEmitter>(TOKEN.InternalEventEmitter)
      .toDynamicValue(() => internalEventEmitter ?? new EnhancedEventEmitter())
      .inSingletonScope();

    container
      .bind<EnhancedEventEmitter>(TOKEN.ClientEventEmitter)
      .toDynamicValue(() => clientEventEmitter ?? new EnhancedEventEmitter())
      .inSingletonScope();

    container.get<IntegrationsService>(TOKEN.IntegrationsService); // instantiate service

    this.clientEventEmitter = container.get<EnhancedEventEmitter>(TOKEN.ClientEventEmitter);

    this.#engine = container.get<Engine>(TOKEN.Engine);
    this.#networkTransportPolicyManager = container
      .get<NetworkTransportPolicyManager>(TOKEN.NetworkTransportPolicyManager);
    this.#clientMetaProvider = container.get<ClientMetaProvider>(TOKEN.ClientMetaProvider);
  }

  /**
   * Метаданные текущей сессии, полезные для аналитики, логирования и диагностики.
   * @group Метаданные
   */
  get meta(): ClientMetaPayload {
    return this.#clientMetaProvider.meta;
  }

  /**
   * Набор пользовательских событий SDK, на которые приложение может подписываться.
   * @group События
   */
  get observer(): EnhancedEventEmitter<ClientObserverEvents> {
    return this.clientEventEmitter;
  }

  /**
   * Идентификатор текущего участника после успешного подключения к каналу.
   * @group Подключение
   */
  get id(): string | undefined {
    return this.#engine.myPeerId;
  }

  /**
   * Список участников, известных клиенту в текущем канале.
   *
   * @remarks Список актуален только после получения события `channel-state-synced`.
   * До этого момента массив может быть неполным или пустым.
   * @group Подключение
   */
  get peers(): Peer[] {
    return this.#engine.getPeers();
  }

  /**
   * Список видеоустройств, обнаруженных после последнего вызова `detectDevices()`.
   * @group Устройства
   */
  get availableVideoDevices(): MediaDeviceInfo[] {
    return this.#engine.system.availableVideoDevices;
  }

  /**
   * Список аудиоустройств, обнаруженных после последнего вызова `detectDevices()`.
   * @group Устройства
   */
  get availableAudioDevices(): MediaDeviceInfo[] {
    return this.#engine.system.availableAudioDevices;
  }

  /**
   * Показывает, был ли успешно подготовлен движок видеоэффектов.
   * @group Медиа
   */
  get effectsSDKInitialized(): boolean {
    return this.#engine.media.effectsSDKInitialized;
  }

  /**
   * Экземпляр калькулятора качества, который можно использовать в собственных метриках UI.
   * @group Диагностика
   */
  get scoresCalculator(): ScoresCalculator {
    return this.#engine.scoresCalculator;
  }

  /**
   * Обнаруживает доступные устройства ввода и обновляет внутренний список камер и микрофонов.
   *
   * @param force Принудительно запрашивает повторное определение устройств, даже если список уже известен.
   * @group Устройства
   * @example
   * ```ts
   * const { video, audio } = await client.detectDevices();
   * // video и audio — массивы MediaDeviceInfo
   * const camera = video[0];
   * const mic = audio.find(d => d.label.includes('встроенный'));
   * ```
   */
  async detectDevices(force?: boolean): Promise<AvailableMediaDevices> {
    try {
      await this.#engine.system.detectDevices(force);
    } catch (error) {
      throw new OperationError('detect_devices_failed', error);
    }

    return {
      video: this.#engine.system.availableVideoDevices,
      audio: this.#engine.system.availableAudioDevices,
    };
  }

  /**
   * Предварительно инициализирует SDK видеоэффектов.
   *
   * Полезно вызывать заранее, если приложение хочет уменьшить задержку перед
   * первым включением background blur или виртуального фона.
   * @group Медиа
   */
  async initEffectsSDK(): Promise<boolean> {
    try {
      return await this.#engine.media.initEffectsSDK();
    } catch (error) {
      throw new OperationError('init_effects_sdk_failed', error);
    }
  }

  /**
   * Подключает клиента к каналу.
   *
   * Канал и роль участника определяются сервером на основе переданного `token`.
   * После успешного завершения необходимо вызвать `requestChannelStateSync()`,
   * чтобы получить актуальное состояние канала через событие `channel-state-synced`.
   * @group Подключение
   * @example
   * ```ts
   * client.observer.on('channel-state-synced', () => {
   *   // Только здесь client.peers содержит актуальный список участников
   *   console.log('Участников в канале:', client.peers.length);
   * });
   *
   * await client.join({ token: signalingToken });
   * client.requestChannelStateSync(); // обязательно после join
   * ```
   */
  async join(params: JoinChannelParams): Promise<void> {
    await this.#engine.join(params);
  }

  /**
   * Выходит из канала.
   *
   * @param keepTracks Если `true`, локальные треки не будут автоматически уничтожены после выхода.
   * @group Подключение
   */
  async leave(keepTracks?: boolean): Promise<void> {
    await this.#engine.release(keepTracks);
  }

  /**
   * Подтверждает активность в ответ на событие `channel-activity-confirmation-required`.
   *
   * Событие рассылается **всем участникам канала** одновременно. Если хотя бы один из них
   * вызовет `confirmActivity()` до истечения таймаута, канал продолжит работу и все участники
   * получат `channel-activity-confirmation-acquired`.
   * Если никто не ответит — канал закрывается целиком и все участники получают
   * `channel-activity-confirmation-expired`.
   * @group Подключение
   */
  async confirmActivity(): Promise<void> {
    await this.#engine.confirmActivity();
  }

  /**
   * Создаёт локальный видеотрек с камеры, готовый к публикации в канале.
   * @group Медиа
   * @example
   * ```ts
   * const track = await client.createCameraVideoTrack({ deviceId: camera.deviceId });
   * await track.publish();
   *
   * // Включить background blur после публикации
   * await track.enableEffects();
   * await track.applyEffects({ blur: 0.7 });
   * ```
   */
  async createCameraVideoTrack(options?: CreateCameraVideoTrackOptions): Promise<VideoTrack> {
    return await this.#engine.createCameraVideoTrack(options);
  }

  /**
   * Создаёт локальный аудиотрек с микрофона, при необходимости сразу с шумоподавлением.
   * @group Медиа
   * @example
   * ```ts
   * const track = await client.createMicrophoneAudioTrack({
   *   deviceId: mic.deviceId,
   *   noiseSuppression: true, // включить шумоподавление сразу
   * });
   * await track.publish();
   * ```
   */
  async createMicrophoneAudioTrack(
    options?: CreateMicrophoneAudioTrackOptions,
  ): Promise<AudioTrack> {
    return await this.#engine.createMicrophoneAudioTrack(options);
  }

  /**
   * Создаёт набор треков для демонстрации экрана и, при наличии, системного аудио.
   * @group Медиа
   */
  async createScreenMediaTracks(options?: CreateScreenMediaOptions): Promise<Track[]> {
    return await this.#engine.createScreenMediaTracks(options);
  }

  /**
   * Регистрирует в SDK уже созданный приложением аудиотрек, чтобы им можно было управлять и публиковать его.
   * @group Медиа
   */
  createCustomAudioTrack(options: CreateCustomAudioOptions): Track {
    return this.#engine.createCustomAudioTrack(options);
  }

  /**
   * Регистрирует в SDK уже созданный приложением видеотрек, чтобы им можно было управлять и публиковать его.
   * @group Медиа
   */
  createCustomVideoTrack(options: CreateCustomVideoOptions): Track {
    return this.#engine.createCustomVideoTrack(options);
  }

  /**
   * Удаляет локальный трек из жизненного цикла SDK и освобождает связанные ресурсы.
   * @group Медиа
   */
  async deleteTrack(track: Track): Promise<void> {
    await this.#engine.deleteTrack(track);
  }

  /**
   * @deprecated Используйте `requestChannelStateSync()` вместо этого метода.
   * @group Подключение
   * `loadPeers` не является потокобезопасным и допускает состояние гонки между
   * двумя последовательными вызовами. `requestChannelStateSync()` гарантирует
   * атомарное получение состояния канала через событие `channel-state-synced`.
   */
  async loadPeers(role?: Role): Promise<void> {
    await this.#engine.loadPeers(role);
  }

  /**
   * Запрашивает синхронизацию состояния канала с сервером.
   *
   * Должен быть вызван сразу после `join()`. Когда синхронизация завершится,
   * придёт событие `channel-state-synced` — только после него `client.peers`
   * содержит актуальный список участников и их медиапотоков.
   * @group Подключение
   */
  requestChannelStateSync(): void {
    this.#engine.requestChannelStateSync();
  }

  /**
   * Обновляет пользовательские `appData` текущего участника, чтобы другие клиенты получили новый payload.
   * Максимальный размер `appData` — 1 KB. Заменяет объект целиком, не мерджит с предыдущим.
   * @group Метаданные
   * @example
   * ```ts
   * await client.updateAppData({ role: 'presenter', handRaised: true });
   * // Другие участники получат это через peer.appData
   * ```
   */
  async updateAppData(appData: Record<string, unknown>): Promise<void> {
    await this.#engine.updateAppData(appData);
  }

  /**
   * Возвращает текущее состояние receive/send transport'ов для отладки сетевых проблем.
   * @group Диагностика
   */
  public async getTransportsInfo(): Promise<TransportsStateInfo> {
    try {
      return await this.#engine.network.getTransportsStateInfo();
    } catch (error) {
      throw new OperationError('get_transports_info_failed', error);
    }
  }

  /**
   * Включает или отключает предпочтение relay-маршрутов для WebRTC-транспортов.
   * @group Диагностика
   */
  public setPreferRelay(value: boolean): void {
    this.#engine.network.setPreferRelay(value);
  }

  /**
   * Показывает, включено ли сейчас предпочтение relay-маршрутов.
   * @group Диагностика
   */
  public getPreferRelay(): boolean {
    return this.#engine.network.getPreferRelay();
  }

  /**
   * Меняет ICE transport policy для активных транспортов клиента.
   *
   * Это полезно для диагностики сетевых сценариев, например когда нужно
   * принудительно использовать relay-соединение.
   * @group Диагностика
   */
  public async changeIceTransportPolicy(policy: IceTransportPolicy): Promise<void> {
    try {
      await this.#networkTransportPolicyManager.changeIceTransportPolicy(policy);
    } catch (error) {
      throw new OperationError('change_ice_transport_policy_failed', error);
    }
  }

  /**
   * Принудительно перезапускает ICE на активных транспортных соединениях клиента.
   * @group Диагностика
   */
  public async restartIce(): Promise<void> {
    try {
      const transports = [
        this.#engine.network.sendTransport,
        this.#engine.network.receiveTransport,
        this.#engine.network.signalingTransport,
      ].filter((transport): transport is LDTransport => !!transport);

      await Promise.all(transports.map(async (transport) =>
        await this.#engine.network.restartIce(transport),
      ));
    } catch (error) {
      throw new OperationError('restart_ice_failed', error);
    }
  }

  /**
   * Возвращает агрегированную транспортную статистику WebRTC для мониторинга качества соединения.
   * @group Диагностика
   */
  public transportsStats(): TransportsWebRTCStats | undefined {
    return this.#engine.transportsStats();
  }

  /**
   * Переопределяет адрес signaling-сервера для последующих подключений или переподключения.
   * @group Диагностика
   */
  public setSignalingServerUrl(url: string): void {
    this.#engine.setSignalingServerUrl(url);
  }

  /**
   * Добавляет пользовательские метаданные, которые будут включаться в diagnostics и аналитику SDK.
   * @group Метаданные
   */
  public setMeta(meta: Record<string, unknown>): void {
    this.#clientMetaProvider.setExternalMeta(meta);
  }
}

export default Client;
export { Client };
